import { Select as ChakraSelect } from '@chakra-ui/react';
import type {
    SelectProps
} from '@chakra-ui/react';
import React, {
    forwardRef
} from 'react';

import { BaseControl, type BaseControlProps } from '../base-control/base-control';
import { SelectIcon } from './components/select-icon';

export type SelectControlProps = BaseControlProps<Omit<SelectProps, 'placeholder'>> &
    {
        /**
         * Html select placeholder value for text prompting user when nothing is selected.
         */
        placeholder?: string | null
    };

type ControlProps = Required<SelectControlProps>['controlProps'];

export const SelectControl = forwardRef<HTMLSelectElement, SelectControlProps>(
    (restProps, ref) => {
        return (
            <BaseControl<ControlProps>
                {...restProps}
                hasValuePredicate={hasValuePredicate}
                assignControlProps={(controlProps, props, state) => {
                    return {
                        ...controlProps,
                        placeholder: props.placeholder
                    };
                }}
                renderControl={({
                    icon = <SelectIcon />,
                    children,
                    // @ts-ignore this prop is moved up and not on the base
                    placeholder = null,
                    ...restControlProps
                }) => (
                    <ChakraSelect
                        autoComplete={'off'}
                        variant={'unstyled'}
                        width={'100%'}
                        height={'56px'}
                        icon={icon}
                        placeholder={placeholder}
                        sx={{
                            pl: '16px',
                            pr: '34px'
                        }}
                        {...restControlProps}
                        ref={ref}
                    >
                        {children}
                    </ChakraSelect>
                )}
            />
        );
    });

function hasValuePredicate (value: unknown, props: SelectControlProps): boolean {
    const isEmptyString = typeof props.labelText === 'string' && props.labelText.length > 0;

    if (isEmptyString && props.placeholder !== ' ') {
        return true;
    }

    if (value === '') {
        return false;
    }

    if (props.children) {
        return true;
    }

    if (typeof value !== 'string') {
        return false;
    }

    return value.length > 0;
}
